import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { loanService, kycService } from '../../services/loanService';
import { authService } from '../../services/authService';
import { LoanStatusBadge, KYCStatusBadge } from '../../components/common/StatusBadge';
import { formatCurrency, formatDate } from '../../utils/helpers';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const SERVICES = [
  { icon: '💸', label: 'Quick Loans',    desc: 'Apply for instant credit',          color: '#00C896', to: '/loans/credit-check', live: true  },
  { icon: '📱', label: 'Airtime',        desc: 'Top up any network instantly',       color: '#818CF8', to: null,                  live: false },
  { icon: '📶', label: 'Data Bundles',   desc: 'Buy mobile data at best rates',      color: '#5B8EFF', to: null,                  live: false },
  { icon: '⚡', label: 'Electricity',    desc: 'Pay PHCN/prepaid meter tokens',      color: '#F5A623', to: null,                  live: false },
  { icon: '📺', label: 'Cable TV',       desc: 'Pay DSTV, GOtv & Startimes',        color: '#FF6B6B', to: null,                  live: false },
  { icon: '📊', label: 'Credit Report',  desc: 'Get your CRC credit report',        color: '#00C8D4', to: '/credit-report',      live: true  },
];

function QuickServices() {
  return (
    <div style={{ marginBottom: '1.5rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
        <h2 style={{ fontSize: '0.9rem', fontWeight: 700, color: 'var(--text-1)' }}>Quick Services</h2>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '0.625rem' }}>
        {SERVICES.map(s => {
          const inner = (
            <div
              key={s.label}
              style={{
                padding: '0.875rem',
                background: 'var(--surface)',
                border: `1px solid var(--border)`,
                borderRadius: 'var(--radius)',
                display: 'flex', flexDirection: 'column', gap: '0.35rem',
                cursor: s.live ? 'pointer' : 'default',
                opacity: s.live ? 1 : 0.72,
                transition: 'transform .15s, box-shadow .15s',
                position: 'relative',
                overflow: 'hidden',
              }}
              onMouseEnter={e => { if (s.live) { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = `0 6px 24px ${s.color}25`; } }}
              onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = ''; }}
            >
              <div style={{ width: 36, height: 36, borderRadius: 10, background: `${s.color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.2rem' }}>
                {s.icon}
              </div>
              <div style={{ fontSize: '0.82rem', fontWeight: 700, color: 'var(--text-1)' }}>{s.label}</div>
              <div style={{ fontSize: '0.7rem', color: 'var(--text-3)', lineHeight: 1.4 }}>{s.desc}</div>
              {!s.live && (
                <span style={{ position: 'absolute', top: 8, right: 8, fontSize: '0.55rem', fontWeight: 700, letterSpacing: '0.06em', padding: '1px 6px', borderRadius: 99, background: 'rgba(255,255,255,0.07)', color: 'var(--text-3)', border: '1px solid var(--border)' }}>SOON</span>
              )}
            </div>
          );
          return s.live ? <Link key={s.label} to={s.to} style={{ textDecoration: 'none' }}>{inner}</Link> : <div key={s.label}>{inner}</div>;
        })}
      </div>
    </div>
  );
}

const KYC_ALERT = {
  not_submitted: { msg: 'Complete your KYC verification to unlock loan applications.', cta: 'Start Verification →' },
  pending:       { msg: 'Your KYC is pending review. You can update your details on your profile.', cta: 'Go to Profile →' },
  under_review:  { msg: 'Our team is reviewing your KYC documents. Check your profile for details.', cta: 'View Profile →' },
  rejected:      { msg: 'Your KYC was rejected. Please resubmit your documents.', cta: 'Resubmit KYC →' },
  resubmit:      { msg: 'Additional information is required for your KYC. Please update your profile.', cta: 'Update Profile →' },
};

function StatCard({ value, label, to, color = 'var(--primary)' }) {
  const navigate = useNavigate();
  return (
    <div
      className="stat-card"
      onClick={() => to && navigate(to)}
      style={{ cursor: to ? 'pointer' : 'default', transition: 'transform .15s, box-shadow .15s' }}
      onMouseEnter={e => { if (to) { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = `0 8px 32px ${color}20`; } }}
      onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = ''; }}
    >
      <div className="stat-value" style={{ color }}>{value}</div>
      <div className="stat-label">{label}</div>
      {to && <div style={{ fontSize: '0.65rem', color: 'var(--text-3)', marginTop: '0.35rem' }}>Click to view →</div>}
    </div>
  );
}

function ProfileCompletionBar({ score }) {
  const color = score >= 80 ? '#00C896' : score >= 50 ? '#F5A623' : '#FF4757';
  return (
    <div style={{ padding: '1rem 1.25rem', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', marginBottom: '1.5rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
        <span style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-2)' }}>Profile Completion</span>
        <span style={{ fontSize: '0.8rem', fontWeight: 700, color }}>{score}%</span>
      </div>
      <div style={{ height: 6, background: 'rgba(255,255,255,0.08)', borderRadius: 99, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${score}%`, background: color, borderRadius: 99, transition: 'width 0.5s ease' }} />
      </div>
      {score < 100 && (
        <div style={{ fontSize: '0.75rem', color: 'var(--text-3)', marginTop: '0.4rem' }}>
          {score < 50 ? 'Complete your profile to improve your loan eligibility.' : 'Almost there — a complete profile improves your loan terms.'}
          {' '}<Link to="/profile" style={{ color: 'var(--primary)', fontWeight: 600 }}>Complete now →</Link>
        </div>
      )}
    </div>
  );
}

function NextPaymentCard({ loans }) {
  const navigate = useNavigate();
  const activeLoan = loans.find(l => l.status === 'active');
  if (!activeLoan) return null;

  return (
    <div style={{
      padding: '1.25rem', background: 'linear-gradient(135deg, rgba(0,200,150,0.08), rgba(129,140,248,0.08))',
      border: '1px solid rgba(0,200,150,0.2)', borderRadius: 'var(--radius)',
      marginBottom: '1.5rem', cursor: 'pointer',
    }}
      onClick={() => navigate(`/loans/${activeLoan.id}/repayments`)}
    >
      <div style={{ fontSize: '0.65rem', fontWeight: 700, color: 'var(--primary)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '0.5rem' }}>
        Active Loan
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
        <div>
          <div style={{ fontSize: '1.5rem', fontWeight: 800, color: 'var(--text-1)' }}>
            {formatCurrency(activeLoan.loan_offer?.approved_amount || activeLoan.requested_amount)}
          </div>
          <div style={{ fontSize: '0.8rem', color: 'var(--text-3)', marginTop: '0.15rem' }}>
            {activeLoan.application_number} · {activeLoan.loan_type}
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          {activeLoan.loan_offer && (
            <>
              <div style={{ fontSize: '0.875rem', fontWeight: 600 }}>
                {formatCurrency(activeLoan.loan_offer.monthly_installment)}<span style={{ fontSize: '0.7rem', color: 'var(--text-3)' }}>/mo</span>
              </div>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-3)' }}>{activeLoan.loan_offer.interest_rate}% p.a.</div>
            </>
          )}
          <div style={{ fontSize: '0.7rem', color: 'var(--primary)', marginTop: '0.25rem', fontWeight: 600 }}>View repayments →</div>
        </div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user, isStaff, isChecker, isMaker } = useAuth();

  const { data: profileData } = useQuery({
    queryKey: ['profile'],
    queryFn: () => authService.getProfile().then(r => r.data.data),
  });

  const { data: loansData, isLoading } = useQuery({
    queryKey: ['my-loans'],
    queryFn: () => loanService.getMyLoans({ page_size: 5 }).then(r => r.data.data),
  });

  const { data: kycData } = useQuery({
    queryKey: ['kyc-status'],
    queryFn: () => kycService.getStatus().then(r => r.data.data),
  });

  if (isStaff) {
    const isPureChecker = isChecker && !isMaker;
    return <Navigate to={isPureChecker ? '/admin/checker' : '/admin/dashboard'} replace />;
  }

  const loans = loansData?.results || [];
  const kycStatus = kycData?.status || 'not_submitted';
  const kycAlert = KYC_ALERT[kycStatus];
  const profileCompletion = profileData?.profile_completion ?? null;
  const pendingOffer = loans.find(l => l.status === 'offer_sent');

  return (
    <div>
      <div className="page-header">
        <h1>Welcome back, {user?.first_name || 'User'}!</h1>
        <p>Manage your loans and track your applications</p>
      </div>

      {/* Email not verified banner */}
      {profileData && !profileData.is_email_verified && (
        <div className="alert alert-info" style={{ marginBottom: '1rem' }}>
          <span>✉</span>
          <span>
            Please verify your email address to secure your account.{' '}
            <Link to="/settings" style={{ fontWeight: 600, color: 'var(--primary)' }}>Verify now →</Link>
          </span>
        </div>
      )}

      {/* Pending offer alert */}
      {pendingOffer && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: '0.75rem',
          padding: '0.875rem 1rem', marginBottom: '1rem',
          background: 'rgba(0,200,150,0.08)', border: '1px solid rgba(0,200,150,0.3)',
          borderRadius: 'var(--radius)', cursor: 'pointer',
        }}
          onClick={() => window.location.href = '/loans'}
        >
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#00C896', flexShrink: 0, animation: 'pulse 2s infinite' }} />
          <span style={{ fontSize: '0.875rem', flex: 1 }}>
            You have a loan offer waiting for your response on application <strong>{pendingOffer.application_number}</strong>.
          </span>
          <span style={{ fontSize: '0.75rem', color: '#00C896', fontWeight: 600 }}>View Offer →</span>
        </div>
      )}

      {/* KYC alert */}
      {kycStatus !== 'approved' && kycAlert && (
        <div className="alert alert-info" style={{ marginBottom: '1.5rem' }}>
          <span>ℹ</span>
          <span>
            {kycAlert.msg}{' '}
            <Link to="/profile" style={{ fontWeight: 600, color: 'var(--primary)' }}>{kycAlert.cta}</Link>
          </span>
        </div>
      )}

      {/* Profile completion */}
      {profileCompletion !== null && profileCompletion < 100 && (
        <ProfileCompletionBar score={profileCompletion} />
      )}

      {/* Active loan summary */}
      {loans.length > 0 && <NextPaymentCard loans={loans} />}

      {/* Stats */}
      <div className="grid-3" style={{ marginBottom: '1.5rem' }}>
        <StatCard value={loansData?.count || 0} label="Total Applications" to="/loans" color="#5B8EFF" />
        <StatCard value={loans.filter(l => l.status === 'active').length} label="Active Loans" to="/loans" color="#00C896" />
        <StatCard value={<KYCStatusBadge status={kycStatus} />} label="KYC Status" to="/profile" />
      </div>

      {/* Quick Services */}
      <QuickServices />

      {/* Recent applications */}
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h2 style={{ fontSize: '1rem', fontWeight: 600 }}>Recent Applications</h2>
          <Link to="/loans/credit-check" className="btn btn-primary" style={{ fontSize: '0.8rem' }}>New Application</Link>
        </div>
        {isLoading ? <LoadingSpinner /> : (
          loans.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-2)' }}>
              <p>No loan applications yet.</p>
              <Link to="/loans/credit-check" className="btn btn-primary" style={{ marginTop: '1rem' }}>Apply Now</Link>
            </div>
          ) : (
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr><th>Application #</th><th>Type</th><th>Amount</th><th>Status</th><th>Date</th><th></th></tr>
                </thead>
                <tbody>
                  {loans.map(loan => (
                    <tr key={loan.id}>
                      <td style={{ fontWeight: 500, fontFamily: 'monospace', fontSize: '0.8rem' }}>{loan.application_number}</td>
                      <td style={{ textTransform: 'capitalize', fontSize: '0.875rem' }}>{loan.loan_type}</td>
                      <td style={{ fontWeight: 600 }}>{formatCurrency(loan.approved_amount ?? loan.requested_amount)}</td>
                      <td><LoanStatusBadge status={loan.status} /></td>
                      <td style={{ fontSize: '0.8rem', color: 'var(--text-2)' }}>{formatDate(loan.created_at)}</td>
                      <td><Link to="/loans" style={{ fontSize: '0.8125rem', color: 'var(--primary)' }}>View →</Link></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        )}
      </div>
    </div>
  );
}
